import { getQueryTimeWindow, RecentRelativeTimeRange } from './time';
import { rest } from './rest';
import { getCurrentLanguage } from './translation';

const getSelectorString = (obj) => {
  const arr = [];
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      arr.push(`${key}="${obj[key]}"`);
    }
  }

  return `{${arr.join(', ')}}`;
};

export const sortByRole = (a, b) => ((`${a.role}`).localeCompare(b.role, undefined, { numeric: true, sensitivity: 'base' }));
export const sortByCategory = (a, b) => ((a.category).localeCompare(b.category, undefined, { numeric: true, sensitivity: 'base' }));

const maxDataPoints = 600;

const calcResolution = (timeWindow) => {
  const duration = timeWindow.duration / 1000;
  return Math.max(Math.floor(duration / maxDataPoints), 60); // Never higher than 1m
};

const getQueryObject = (metricObject, timeWindow, uuid) => {
  const {
    metric, metricFields, rate, avg,
  } = metricObject;

  const selector = { postgres_cluster: uuid };
  const selectorString = getSelectorString(selector);

  let query = `${metric}${selectorString}`;

  if (rate) {
    if (metricFields) {
      query = `sum by (${metricFields.join(', ')}) (irate(${metric}${selectorString}[${rate}]) >= 0)`;
    } else {
      query = `(irate(${metric}${selectorString}[${rate}]) >= 0)`;
    }
  } else if (avg) {
    query = `avg by (role, instance) (${metric}${selectorString})`;
  }

  return {
    query,
    start: timeWindow.start.toISOString(),
    end: timeWindow.end.toISOString(),
    step: calcResolution(timeWindow),
  };
};

const normalize = (rawData, {
  filterFn,
  normalizeFn,
}) => {
  if (!rawData.data) return { data: { result: [] } };
  let resultList = rawData.data.result;
  if (filterFn) {
    resultList = resultList.filter(filterFn);
    rawData.data.result = resultList;
  }
  resultList.forEach((result) => {
    result.values = result.values.map((dataPoint) => {
      let value = parseFloat(dataPoint[1]) || 0;
      value = normalizeFn ? normalizeFn(value) : value;
      const date = new Date(dataPoint[0] * 1000);
      return {
        date: date.getTime(),
        value: String(value) === 'NaN' ? 0 : value,
      };
    })
      .sort((a, b) => b.date - a.date);
  });
  rawData.data.result = rawData.data.result.filter((item) => item.metric.role !== 'unknown');
  return rawData;
};

const extractData = (metric) => (rawData) => (
  {
    [metric]: rawData.data.result.reduce((acc, r) => (
      [...acc, {
        role: r.metric.role || 'cluster',
        state: r.metric.state,
        datname: r.metric.datname,
        relname: r.metric.relname,
        schemaname: r.metric.schemaname,
        instance: r.metric.instance,
        values: r.values,
      }]
    ), []),
  }
);

export const createFetchAction = (metric, props) => (fetchParams) => {
  const timeRange = fetchParams.range || RecentRelativeTimeRange({
    unit: 'hours',
    range: 2,
  });
  const timeWindow = getQueryTimeWindow(timeRange);
  const { uuid } = props;
  const queryObject = getQueryObject(metric, timeWindow, uuid);
  const queryString = new URLSearchParams(queryObject).toString();

  return rest.get({
    baseUrl: 'telemetry',
    url: `/query_range?${queryString}`,
  })
    .then((data) => normalize(data, metric))
    .then(extractData(metric.metric));
};

export const getInstanceName = (item) => {
  if (item.role === 'master') return item.role;
  return `${item.role} - ${item.instance.substr(-1)}`;
};

export const computePercentageData = (data, keyTotal, keyAvailable) => {
  if (!keyTotal) {
    return !data[keyAvailable] ? [] : data[keyAvailable].filter((item) => ['master', 'replica'].includes(item.role)).sort(sortByRole).map((item) => {
      const availableValues = item.values;

      const currentAvailable = availableValues[0].value * 100;

      return {
        label: getInstanceName(item),
        data: {
          used: currentAvailable,
          total: 100,
        },
      };
    });
  }
  return !data[keyTotal] ? [] : data[keyTotal].filter((item) => ['master', 'replica'].includes(item.role)).sort(sortByRole).map((item) => {
    const totalValues = item.values;
    const availableValues = data[keyAvailable]?.find((innerItem) => innerItem.instance === item.instance && item.role === innerItem.role)?.values;

    const currentAvailable = availableValues[0];
    const currentTotal = totalValues.find((totalItem) => totalItem.date === currentAvailable.date) || totalValues[0];

    return {
      label: getInstanceName(item),
      data: {
        used: currentTotal.value - currentAvailable.value,
        total: currentTotal.value,
      },
    };
  });
};

export const computeUsedData = (data, keyTotal, keyAvailable) => (
  !data[keyTotal] ? [] : data[keyTotal].filter((item) => ['master', 'replica'].includes(item.role)).sort(sortByRole).map((item) => {
    const totalValues = item.values;
    const availableValues = data[keyAvailable]?.find((innerItem) => innerItem.instance === item.instance && item.role === innerItem.role)?.values;

    const dataValues = availableValues.map((availableItem) => {
      const total = totalValues.find((totalItem) => totalItem.date === availableItem.date);
      return {
        ...availableItem,
        value: total.value - availableItem.value,
      };
    });

    return {
      category: getInstanceName(item),
      dataValues,
    };
  })
);

export const hexToHSL = (H) => {
// Convert hex to RGB first
  let [r, g, b] = [0, 0, 0];
  if (H.length === 4) {
    r = `0x${H[1]}${H[1]}`;
    g = `0x${H[2]}${H[2]}`;
    b = `0x${H[3]}${H[3]}`;
  } else if (H.length === 7) {
    r = `0x${H[1]}${H[2]}`;
    g = `0x${H[3]}${H[4]}`;
    b = `0x${H[5]}${H[6]}`;
  }
  // Then to HSL
  r /= 255;
  g /= 255;
  b /= 255;
  const cmin = Math.min(r, g, b);
  const cmax = Math.max(r, g, b);
  const delta = cmax - cmin;
  let [h, s, l] = [0, 0, 0];

  if (delta === 0) {
    h = 0;
  } else if (cmax === r) {
    h = ((g - b) / delta) % 6;
  } else if (cmax === g) {
    h = (b - r) / delta + 2;
  } else {
    h = (r - g) / delta + 4;
  }

  h = Math.round(h * 60);

  if (h < 0) {
    h += 360;
  }

  l = (cmax + cmin) / 2;
  s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));
  s = +(s * 100).toFixed(1);
  l = +(l * 100).toFixed(1);

  return {
    h,
    s,
    l,
  };
};

export const HSLToHex = ({
  h,
  s,
  l,
}) => {
  s /= 100;
  l /= 100;

  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r = 0;
  let g = 0;
  let b = 0;

  if (h >= 0 && h < 60) {
    r = c;
    g = x;
    b = 0;
  } else if (h >= 60 && h < 120) {
    r = x;
    g = c;
    b = 0;
  } else if (h >= 120 && h < 180) {
    r = 0;
    g = c;
    b = x;
  } else if (h >= 180 && h < 240) {
    r = 0;
    g = x;
    b = c;
  } else if (h >= 240 && h < 300) {
    r = x;
    g = 0;
    b = c;
  } else if (h >= 300 && h < 360) {
    r = c;
    g = 0;
    b = x;
  }
  // Having obtained RGB, convert channels to hex
  r = Math.round((r + m) * 255)
    .toString(16);
  g = Math.round((g + m) * 255)
    .toString(16);
  b = Math.round((b + m) * 255)
    .toString(16);

  // Prepend 0s, if necessary
  if (r.length === 1) {
    r = `0${r}`;
  }
  if (g.length === 1) {
    g = `0${g}`;
  }
  if (b.length === 1) {
    b = `0${b}`;
  }

  return `#${ r }${g }${b}`;
};

export const stepValueInRange = ({
  min,
  max,
  step,
  rounds = 0,
}) => (value, stepMultiplier) => {
  const newValue = value - (stepMultiplier * step);
  if (newValue < min) {
    return stepValueInRange({
      min,
      max,
      step,
      rounds: rounds + 1,
    })(max - (min - value), stepMultiplier);
  }
  return { value: newValue, rounds };
};

export const getColorInRange = ({
  color,
  min = 30,
  max = 60,
  step = 2,
}) => (index) => {
  const hslColor = hexToHSL(color);
  const { h, s, l } = hslColor;

  const { value, rounds } = stepValueInRange({
    min,
    max,
    step,
  })(l, index);

  const { value: hValue } = stepValueInRange({
    min: 70,
    max: 390,
    step: 30,
  })(h, rounds);

  return HSLToHex({ h: hValue, s, l: value });
};

export const splitId = (name) => name.split('__')[0];

export const getOpacity = (val) => (val ? 1 : 0.1);

export const dataValueSortFunction = (itemA, itemB) => (
  Math.max(...itemB.dataValues.map((innerItem) => innerItem.value)) - Math.max(...itemA.dataValues.map((innerItem) => innerItem.value))
);

const decimalChar = getCurrentLanguage() === 'de' ? ',' : '.';
export const getValueWithDecimalChar = (val) => val.replace(',', decimalChar).replace('.', decimalChar);
